import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaColumns, FaFilter } from "react-icons/fa";
import { IoMdClose, IoIosArrowBack } from "react-icons/io";
import {
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import ModifyColumns from "../Tools/ModifyColumns";
import {
  apiRequestBodyBuilder,
  getCurrencySymbol,
} from "../config/tableHelper";

// Skeleton styled identically to PaidAdsPage TableSkeleton
function TableSkeleton() {
  return (
    <div className="w-full rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      {/* Header Bar */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-pulse">
        <div className="h-4 w-32 bg-gray-200 rounded" />
        <div className="flex items-center gap-8 sm:gap-12">
          <div className="h-4 w-16 bg-gray-200 rounded hidden sm:block" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-200 rounded hidden md:block" />
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 animate-pulse">
        {[1, 2, 3, 4, 5, 6].map((item) => (
          <div key={item} className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div
                className="h-4 bg-gray-200 rounded"
                style={{ width: `${140 + (item % 3) * 35}px` }}
              />
            </div>
            <div className="flex items-center gap-8 sm:gap-12">
              <div className="h-4 w-14 bg-gray-100 rounded hidden sm:block" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-16 bg-gray-100 rounded" />
              <div className="h-4 w-20 bg-gray-100 rounded hidden md:block" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * INTERNAL FILTER COMPONENT
 * Matches the Centered Modal UI of tableHelper
 */
const BudgetFilterComponent = ({
  data,
  appliedFilters,
  onApplyFilter,
  onRemoveFilter,
  showDropdown,
  setShowDropdown,
}) => {
  const [selectedFilterKey, setSelectedFilterKey] = useState(null);
  const [numericValue, setNumericValue] = useState("");
  const [comparison, setComparison] = useState(">");
  const [selectedMultiOptions, setSelectedMultiOptions] = useState([]);

  // Filter Categories defined for this specific table
  const filterOptions = [
    { key: "budget_name", title: "Budget Name", type: "boolean" },
    { key: "display_field", title: "Budget Type", type: "boolean" },
    { key: "total_budget_raw", title: "Total Budget", type: "number" },
  ];

  const closePanel = () => {
    setShowDropdown(false);
    setSelectedFilterKey(null);
    setNumericValue("");
    setSelectedMultiOptions([]);
  };

  const handleApply = () => {
    const col = filterOptions.find((c) => c.key === selectedFilterKey);
    if (!col) return;

    let filterLabel = "";
    let filterValue = null;

    if (col.type === "number") {
      if (!numericValue) return;
      filterValue = { comparison, value: parseFloat(numericValue) };
      filterLabel = `${col.title} ${comparison} ${numericValue}`;
    } else {
      if (selectedMultiOptions.length === 0) return;
      filterValue = selectedMultiOptions;
      filterLabel = `${col.title}: ${selectedMultiOptions.join(", ")}`;
    }

    onApplyFilter({
      id: Date.now(),
      key: col.key,
      type: col.type,
      label: filterLabel,
      value: filterValue,
    });
    closePanel();
  };

  const getUniqueOptions = (key) => {
    return [...new Set(data.map((item) => item[key]).filter(Boolean))].sort();
  };

  const stopPropagation = (e) => e.stopPropagation();

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => setShowDropdown(true)}
        className="flex items-center px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-gray-700 text-sm font-medium transition-colors shadow-sm focus:outline-none"
      >
        <FaFilter className="mr-2 text-gray-500" /> Add filter
      </button>

      <div className="flex items-center gap-2 flex-wrap">
        {appliedFilters.map((f) => (
          <span
            key={f.id}
            className="bg-blue-50 border border-blue-200 text-blue-700 px-3 py-1 rounded-md text-sm font-medium flex items-center shadow-sm"
          >
            {f.label}
            <button
              className="ml-2 text-blue-400 hover:text-blue-700"
              onClick={() => onRemoveFilter(f.id)}
            >
              <IoMdClose />
            </button>
          </span>
        ))}
      </div>

      {showDropdown &&
        createPortal(
          <div
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
            onClick={closePanel}
          >
            <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"></div>
            <div
              className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
              onClick={stopPropagation}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  {selectedFilterKey && (
                    <button
                      onClick={() => setSelectedFilterKey(null)}
                      className="text-gray-400 hover:text-gray-800 p-1"
                    >
                      <IoIosArrowBack size={20} />
                    </button>
                  )}
                  <h2 className="text-lg font-semibold text-gray-800">
                    {!selectedFilterKey
                      ? "Filter by"
                      : filterOptions.find((c) => c.key === selectedFilterKey)
                          ?.title}
                  </h2>
                </div>
                <button
                  onClick={closePanel}
                  className="text-gray-400 hover:text-gray-700"
                >
                  <IoMdClose size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto bg-slate-50 p-6">
                {!selectedFilterKey ? (
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    {filterOptions.map((opt) => (
                      <button
                        key={opt.key}
                        className="w-full text-left px-5 py-3.5 hover:bg-gray-50 text-sm font-medium text-gray-700 border-b border-gray-100 last:border-0 transition-colors"
                        onClick={() => setSelectedFilterKey(opt.key)}
                      >
                        {opt.title}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {selectedFilterKey === "total_budget_raw" ? (
                      <div className="flex gap-2">
                        <select
                          value={comparison}
                          onChange={(e) => setComparison(e.target.value)}
                          className="w-1/3 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        >
                          <option value=">">Greater</option>
                          <option value="<">Less</option>
                          <option value="=">Equal</option>
                        </select>
                        <input
                          type="number"
                          value={numericValue}
                          onChange={(e) => setNumericValue(e.target.value)}
                          placeholder="Amount..."
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        />
                      </div>
                    ) : (
                      <FormControl fullWidth size="small">
                        <Select
                          multiple
                          value={selectedMultiOptions}
                          onChange={(e) =>
                            setSelectedMultiOptions(e.target.value)
                          }
                          displayEmpty
                          renderValue={(selected) =>
                            selected.length ? selected.join(", ") : "Select..."
                          }
                          MenuProps={{ style: { zIndex: 100001 } }}
                          className="bg-white"
                        >
                          {getUniqueOptions(selectedFilterKey).map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              <Checkbox
                                checked={selectedMultiOptions.includes(opt)}
                              />
                              <ListItemText primary={opt} />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => setSelectedFilterKey(null)}
                        className="flex-1 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleApply}
                        className="flex-[2] py-2 text-sm font-medium text-white bg-[#283593] rounded-lg"
                      >
                        Apply Filter
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

/**
 * MAIN COMPONENT
 */
export default function BudgetTable({ initialDateRange, PageType }) {
  const [unfilteredData, setUnfilteredData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState([]);

  const [columns, setColumns] = useState([
    {
      id: "5",
      title: "Budget Name",
      key: "budget_name",
      visible: true,
      locked: true,
      isLocked: true,
      category: "Recommended",
      type: "string",
    },
    {
      id: "1",
      title: "Email",
      key: "email",
      visible: false,
      category: "Recommended",
      type: "string",
    },
    {
      id: "4",
      title: "Budget Type",
      key: "display_field",
      visible: true,
      category: "Recommended",
      type: "string",
    },
    {
      id: "2",
      title: "Start Date",
      key: "start_date",
      visible: true,
      category: "Recommended",
      type: "string",
    },
    {
      id: "4",
      title: "End Date",
      key: "end_date",
      visible: true,
      category: "Recommended",
      type: "string",
    },
    {
      id: "3",
      title: "Total Budget",
      key: "total_budget",
      visible: true,
      category: "Performance",
      type: "currency",
    },
    {
      id: "6",
      title: "Google Ads Budget",
      key: "google_ads_budget",
      visible: true,
      category: "Performance",
      type: "percentage",
    },
    {
      id: "7",
      title: "Bing Ads Budget",
      key: "bing_ads_budget",
      visible: true,
      category: "Performance",
      type: "percentage",
    },
  ]);

  const [groups, setGroups] = useState([]);
  const columnRef = useRef(null);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      let requestBody = apiRequestBodyBuilder();
      const responseInfo = await fetch(
        "https://api.confidanto.com/campaign-group2/group-info",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      if (!responseInfo.ok) throw new Error("Failed group info");
      const groupType = await responseInfo.json();
      const typeForQuery =
        groupType?.type === "keyword"
          ? "keywords"
          : (groupType?.type ?? "campaign");

      requestBody.type = typeForQuery;
      const response = await fetch(
        "https://api.confidanto.com/campaign-group/fetch-groups",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      const data = await response.json();
      setGroups(data.groups || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        let resBody = apiRequestBodyBuilder(initialDateRange, {
          projectId: localStorage.getItem("project_id"),
        });
        const response = await fetch(
          "https://api.confidanto.com/monthly-budget/get",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(resBody),
          },
        );

        if (!response.ok) throw new Error("Failed to fetch data");
        const result = await response.json();

        const currencySymbol = getCurrencySymbol();

        let structuredData = result.map((item) => {
          const findGroupName = (groupId) => {
            const group = groups.find(
              (g) => String(g.group_id) === String(groupId),
            );
            return group?.group_name;
          };

          const typeDisplay =
            item.campaign_type ?? findGroupName(item.group_id) ?? "Overall";

          const rawTotal = Number(item.total_budget) || 0;

          let formattedItem = {
            ...item,
            display_field: typeDisplay,
            total_budget_raw: rawTotal,
            total_budget: `${currencySymbol}${rawTotal.toLocaleString()}`,
          };

          if (item.google_ads_budget !== null) {
            formattedItem.google_ads_budget = `${currencySymbol}${
              (Number(item.google_ads_budget) * rawTotal) / 100
            }`;
          }
          if (item.bing_ads_budget !== null) {
            formattedItem.bing_ads_budget = `${currencySymbol}${
              (Number(item.bing_ads_budget) * rawTotal) / 100
            }`;
          }

          return formattedItem;
        });

        setUnfilteredData(structuredData);
        setFilteredData(structuredData);

        const platforms = JSON.parse(
          localStorage.getItem("platform_connected") || "{}",
        );
        setColumns((prev) =>
          prev.map((col) => {
            if (col.key === "google_ads_budget")
              return { ...col, visible: !!platforms?.google_ads };
            if (col.key === "bing_ads_budget")
              return { ...col, visible: !!platforms?.microsoft_ads };
            return col;
          }),
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    setAppliedFilters([]);

    if (groups.length >= 0) fetchData();
  }, [
    groups,
    initialDateRange[0].startDate,
    initialDateRange[0].endDate,
    initialDateRange.length,
  ]);

  useEffect(() => {
    if (appliedFilters.length === 0) {
      setFilteredData(unfilteredData);
      return;
    }

    const filtered = unfilteredData.filter((row) => {
      return appliedFilters.every((filter) => {
        const val = row[filter.key];
        if (filter.type === "number") {
          const { comparison, value } = filter.value;
          if (comparison === ">") return val > value;
          if (comparison === "<") return val < value;
          return val === value;
        } else {
          return filter.value.includes(val);
        }
      });
    });
    setFilteredData(filtered);
  }, [appliedFilters, unfilteredData]);

  const handleApplyFilter = (newFilter) => {
    setAppliedFilters((prev) => [...prev, newFilter]);
  };

  const handleRemoveFilter = (id) => {
    setAppliedFilters((prev) => prev.filter((f) => f.id !== id));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (columnRef.current && !columnRef.current.contains(event.target)) {
        setShowColumnsMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const visibleColumns = columns.filter((col) => col.visible);

  return (
    <div className="flex h-full bg-gray-100 font-roboto">
      <main className="w-full p-4">
        <div className="flex justify-between items-center mb-3">
          <BudgetFilterComponent
            data={unfilteredData}
            appliedFilters={appliedFilters}
            onApplyFilter={handleApplyFilter}
            onRemoveFilter={handleRemoveFilter}
            showDropdown={showFilterDropdown}
            setShowDropdown={setShowFilterDropdown}
          />
          <div className="relative" ref={columnRef}>
            <button
              disabled={loading}
              className={`bg-transparent text-gray-600 px-3 py-1.5 rounded flex flex-col items-center justify-center transition-all ${
                loading ? "opacity-50 cursor-not-allowed" : "hover:bg-slate-100"
              }`}
              onClick={() => setShowColumnsMenu(!showColumnsMenu)}
            >
              <FaColumns className="text-sm" />
              <span className="text-sm">Columns</span>
            </button>
            {showColumnsMenu && (
              <ModifyColumns
                columns={columns}
                setColumns={setColumns}
                setShowColumnsMenu={setShowColumnsMenu}
                colsLimit={13}
              />
            )}
          </div>
        </div>

        {/* Table & Skeleton Loading */}
        {loading ? (
          <TableSkeleton />
        ) : error ? (
          <div className="overflow-x-auto bg-white rounded shadow">
            <div className="flex justify-center items-center h-40 text-red-500">
              Error: {error}
            </div>
          </div>
        ) : filteredData.length > 0 ? (
          <div className="overflow-x-auto bg-white rounded shadow">
            <table className="min-w-full table-auto border-collapse border border-gray-200 bg-white rounded">
              <thead className="bg-[#283593] text-white">
                <tr>
                  {visibleColumns.map((col) => (
                    <th key={col.key} className="border py-2 px-4 text-center">
                      <span className="text-sm font-light">{col.title}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item, i) => (
                  <tr key={item.id || i} className="border-b hover:bg-gray-50">
                    {visibleColumns.map((col) => (
                      <td
                        key={col.key}
                        className="border px-3 py-1.5 text-sm text-center"
                      >
                        {item[col.key] ?? "–"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td
                    colSpan={visibleColumns.length}
                    className="px-3 py-2 text-sm text-gray-600 text-right font-medium"
                  >
                    Showing {filteredData.length} records
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded shadow">
            <div className="flex justify-center items-center h-40">
              <h2 className="text-gray-500">No Budget Data Found</h2>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
